import { Link } from "react-router-dom";
import { ArrowRight, Anchor, Wrench, Mountain, Bike, Map, PartyPopper, Sparkles } from "lucide-react";
import { VERTICALS } from "@/data/verticals";
import CTABand from "@/components/CTABand";
import { useSeo } from "@/lib/seo";
import { useReveal } from "@/lib/reveal";

const ICONS = {
  marine: Anchor,
  equipment: Wrench,
  recreation: Mountain,
  powersports: Bike,
  tours: Map,
  events: PartyPopper,
};

export default function Industries() {
  useSeo({
    title: "Industries · Built for every rental vertical",
    description:
      "RentAnything.ai powers marine, equipment, recreation, powersports, tours and events. One platform, every vertical, no rebuild.",
    path: "/industries",
  });
  useReveal();

  return (
    <>
      <section className="ra-mesh">
        <div className="ra-container py-20 sm:py-28">
          <div className="max-w-3xl">
            <span className="ra-pill">
              <Sparkles size={12} className="text-ra-amber" /> 6 verticals · 1 platform
            </span>
            <h1 className="mt-5 ra-h1">Rent anything. Literally.</h1>
            <p className="mt-5 ra-lead">
              The whole point: one configurable engine that bends to every vertical we've seen. Pick yours below — every page is live with vertical-specific workflows, copy, and use cases.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="ra-container">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {VERTICALS.map((v) => {
              const Icon = ICONS[v.slug] ?? Sparkles;
              return (
                <Link
                  key={v.slug}
                  to={`/industries/${v.slug}`}
                  className="group relative overflow-hidden rounded-3xl border border-ra-line bg-white p-7 shadow-card transition-all hover:-translate-y-1 hover:shadow-soft reveal"
                  data-testid={`industry-card-${v.slug}`}
                >
                  <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${v.accent}`} />
                  <div className="relative">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ra-ocean/10 text-ra-ocean">
                      <Icon size={22} />
                    </div>
                    <h2 className="mt-5 font-display text-xl font-semibold text-ra-ink">{v.name}</h2>
                    <p className="mt-1 text-sm text-ra-ink/70">{v.tagline}</p>
                    <p className="mt-5 text-sm text-ra-ink/80 leading-relaxed">{v.sub}</p>
                    <span className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-ra-ocean">
                      Explore {v.name.toLowerCase()} <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand
        eyebrow="Don't see your vertical?"
        title="Tell us what you rent — we'll configure it."
        sub="If a rental has a duration, a unit, and a customer, RentAnything can model it. Test us."
      />
    </>
  );
}
